import { FC, MutableRefObject, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  SearchableUsersList,
  UserOptionWithCheckedStatus,
} from '@getsynapse/design-system';
import intl from 'react-intl-universal';

interface AssigneesDropdownProps {
  assignees: UserOptionWithCheckedStatus[];
  onChange: (userId: string) => void;
  onSearchChange?: (searchTerm: string) => void;
  isDropdownOpen?: boolean;
  cellRef?: MutableRefObject<HTMLDivElement | null>;
  dropdownContainerRef?: MutableRefObject<HTMLDivElement | null>;
  isFetchingAvailableAssignees: boolean;
}

export const AssigneesDropdown: FC<AssigneesDropdownProps> = ({
  assignees,
  onChange,
  onSearchChange,
  isDropdownOpen,
  cellRef,
  dropdownContainerRef,
  isFetchingAvailableAssignees,
}) => {
  useEffect(() => {
    if (isDropdownOpen && dropdownContainerRef?.current) {
      requestAnimationFrame(() => {
        if (!dropdownContainerRef?.current) return;

        // Find the focusable element inside SearchableUsersList
        const focusableElement = dropdownContainerRef.current.querySelector(
          '[tabindex="0"]'
        ) as HTMLElement;

        if (focusableElement) {
          focusableElement.focus();
        }
      });
    }
  }, [isDropdownOpen, dropdownContainerRef]);

  if (!isDropdownOpen || !cellRef?.current || typeof document === 'undefined') {
    return null;
  }

  const rect = cellRef.current.getBoundingClientRect();

  return createPortal(
    <div
      ref={dropdownContainerRef}
      style={{
        position: 'fixed',
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      }}
      className='pointer-events-auto z-50'
      onClick={(e) => e.stopPropagation()}
    >
      <SearchableUsersList
        users={assignees}
        onUserSelectionChange={onChange}
        onSearchChange={onSearchChange}
        isLoading={isFetchingAvailableAssignees}
        deactivatedUserText={intl.get('DEACTIVATED')}
      />
    </div>,
    document.body
  );
};
